import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import ProductivityCharts from './ProductivityCharts';
import InsightsPanel from './InsightsPanel';
import MetricsCards from './MetricsCards';

interface AnalyticsData {
  summary: any;
  hourlyPatterns: any;
  dailyTrends: any[];
  weeklyPatterns: any[];
  categoryBreakdown: any[];
  priorityAnalysis: any[];
  streaks: any;
  achievements: any[];
  timeUtilization: any;
  period: any;
}

interface Insight {
  type: string;
  title: string;
  message: string;
  actionable: string;
  priority: string;
}

const AnalyticsDashboard: React.FC = () => {
  const { user } = useAuth();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnalytics();
    fetchInsights();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      
      const data = await apiService.getAnalyticsDashboard(timeRange);
      console.log('Analytics data:', data);
      
      if (data.success) {
        setAnalyticsData(data.analytics);
      } else {
        throw new Error(data.error || 'Failed to load analytics');
      }

    } catch (error: any) {
      console.error('Analytics error:', error);
      setError(error.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchInsights = async () => {
    try {
      const data = await apiService.getAnalyticsInsights(timeRange);
      
      if (data.success) {
        setInsights(data.insights || []);
      }
    } catch (error) {
      console.error('Insights error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="loading" style={{ minHeight: '400px' }}>
          <div className="spinner"></div>
          <span>Loading your analytics...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md">
          <div className="text-center">
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#374151' }}>
              Analytics Unavailable
            </h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={fetchAnalytics}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📈</div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#374151' }}>
            No Analytics Data
          </h2>
          <p className="text-gray-600">Complete some tasks to see your productivity insights!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ padding: '2rem 0' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                📊 Analytics Dashboard
              </h1>
              <p className="text-gray-600">
                Welcome back, {user?.firstName || user?.username}! Here's your productivity overview.
              </p>
            </div>
            
            {/* Time Range Selector */}
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                Time Range
              </label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(parseInt(e.target.value))}
                className="input"
                style={{ width: 'auto', marginBottom: 0 }}
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 3 months</option>
                <option value={180}>Last 6 months</option>
              </select>
            </div>
          </div>

          {/* Period Info */}
          <div style={{ 
            backgroundColor: '#f0f9ff', 
            border: '1px solid #bae6fd', 
            borderRadius: '8px', 
            padding: '12px',
            fontSize: '0.875rem',
            color: '#0369a1'
          }}>
            <strong>Analysis Period:</strong> {analyticsData.period.startDate} to {analyticsData.period.endDate} 
            ({analyticsData.period.days} days)
          </div>
        </div>

        {/* Metrics Cards */}
        <MetricsCards data={analyticsData} />

        {/* Insights Panel */}
        <InsightsPanel insights={insights} />

        {/* Charts */}
        <ProductivityCharts data={analyticsData} />

      </div>
    </div>
  );
};

export default AnalyticsDashboard;
